import { useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { ApiError } from "../api/client";
import {
  errorMessage,
  useCancelCpuRun,
  useCpuRun,
  useCpuRunLogs,
  useExtendCpuRun,
} from "../api/queries";
import { TERMINAL_CPU_RUN_STATES } from "../api/types";
import { ClusterProblem, isClusterProblem } from "../components/ClusterProblem";
import { TaskList } from "../components/cpu/TaskList";
import { TaskMeter } from "../components/cpu/TaskMeter";
import { PageHeader } from "../components/layout/PageHeader";
import { CopyBlock } from "../components/ui/CopyBlock";
import { Dialog } from "../components/ui/Dialog";
import { Field } from "../components/ui/Field";
import { ErrorBlock, LoadingBlock } from "../components/ui/StateBlock";
import { EventTimeline } from "../components/workspace/EventTimeline";
import { MountsList } from "../components/workspace/MountsList";
import {
  cpuRunHeadline,
  cpuRunStateLabel,
  cpuRunTone,
  NEVER_IDLE_NOTE,
  REPRODUCE_NOTE,
  reproduceAsNkCommand,
  SCRATCH_PER_MACHINE_NOTE,
  taskSummary,
} from "../lib/cpu";
import { formatDateTime, formatRemaining, pluralize } from "../lib/format";

/** Extension lengths offered in the dialog. */
const EXTEND_PRESETS: readonly number[] = [1, 2, 4, 8];

/**
 * One run: what it is doing, what it wrote, and the two ways it ends early.
 *
 * There is no connection block and no idle countdown, and neither is missing:
 * a run has no shell to reach, and nothing on a CPU machine could tell an
 * idle run from a busy one anyway.
 *
 * A run of several tasks is the same screen with one number chosen: the log
 * shown is **one task's**, because there is no merged log — a thousand
 * interleaved tasks are a file in which no single task is readable, which is
 * the opposite of what somebody debugging one failed shard needs.
 *
 * Returns:
 *     The run screen.
 */
export function CpuRunPage(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const run = useCpuRun(id);
  const cancel = useCancelCpuRun(id);
  const extend = useExtendCpuRun(id);
  const [task, setTask] = useState(0);
  const logs = useCpuRunLogs(id, task, Boolean(run.data?.has_logs));
  const [dialog, setDialog] = useState<"extend" | "stop" | null>(null);
  const [hours, setHours] = useState<number>(EXTEND_PRESETS[1] ?? 2);
  const logTitle = useRef<HTMLHeadingElement>(null);

  const live =
    run.data !== undefined && !TERMINAL_CPU_RUN_STATES.includes(run.data.state);

  const close = (): void => {
    setDialog(null);
    extend.reset();
    cancel.reset();
  };

  const header = (
    <PageHeader
      eyebrow="Cores"
      title={run.data?.name ?? "Run"}
      back={{ to: "/cpu", label: "CPU" }}
      subtitle={run.data ? run.data.image : undefined}
      actions={
        live ? (
          <div className="actions">
            <button
              type="button"
              className="btn"
              onClick={() => setDialog("extend")}
            >
              Add time
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => setDialog("stop")}
            >
              Stop
            </button>
          </div>
        ) : null
      }
    />
  );

  if (run.isPending) {
    return (
      <>
        {header}
        <LoadingBlock label="Loading this run…" />
      </>
    );
  }

  if (run.isError) {
    if (isClusterProblem(run.error)) {
      return (
        <>
          {header}
          <ClusterProblem error={run.error} onRetry={() => void run.refetch()} />
        </>
      );
    }
    if (run.error instanceof ApiError && run.error.isNotFound) {
      return (
        <>
          {header}
          <p className="state-block">
            This run does not exist any more, or it belongs to somebody else.
          </p>
        </>
      );
    }
    return (
      <>
        {header}
        <ErrorBlock error={run.error} onRetry={() => void run.refetch()} />
      </>
    );
  }

  const data = run.data;
  const counters = taskSummary(data);
  // A task to choose only where there are several *and* something to read: a
  // run whose first task has not started has no log at all, and offering a
  // number to pick would suggest otherwise.
  const perTaskLogs = data.tasks > 1 && data.has_logs;
  const nkCommand = reproduceAsNkCommand(data);

  /** Keep the chosen task inside the run, so no known-404 is ever fetched. */
  const chooseTask = (raw: string): void => {
    const candidate = Number(raw);
    if (Number.isInteger(candidate) && candidate >= 0 && candidate < data.tasks) {
      setTask(candidate);
    }
  };

  /**
   * Show one task's log in the single panel this page has.
   *
   * The list and the number field point at the same panel deliberately: two
   * places showing one task's output can disagree, and one of them would be
   * wrong. Focus follows the click, because the panel is further down the page
   * than the row that asked for it.
   */
  const showLog = (index: number): void => {
    setTask(index);
    logTitle.current?.focus();
  };

  return (
    <>
      {header}

      <section className="state-line" data-tone={cpuRunTone(data)}>
        <span className="badge" data-tone={cpuRunTone(data)}>
          <span className="badge__dot" aria-hidden="true" />
          {cpuRunStateLabel(data.state)}
        </span>
        <p className="state-line__text">{cpuRunHeadline(data)}</p>
        {/* The counters, not a second headline: a run of a thousand tasks is
            150 waiting, 3 running and 847 finished at once, and no single
            state could stand for that. The bar under them is that sentence
            drawn, never a second reading of it. */}
        {counters ? <p className="state-line__note">{counters}</p> : null}
        {counters ? <TaskMeter run={data} /> : null}
      </section>

      {/* A failed stop is shown inside the dialog that asked for it, and
          nowhere else: the dialog stays open on failure, and repeating the
          server's sentence on the page behind it says the same thing twice. */}

      <section className="section">
        <p className="job-detail__meta">
          {data.tasks > 1
            ? `${pluralize(data.cores, "core")} per task`
            : pluralize(data.cores, "core")}{" "}
          · run {data.run_number} · submitted {formatDateTime(data.created_at)}
          {data.machines > 0
            ? ` · on ${pluralize(data.machines, "machine")}`
            : ""}
          {data.expires_at ? ` · stops in ${formatRemaining(data.expires_at)}` : ""}
        </p>
        <pre className="log log--command">{data.command}</pre>
        <p className="form__note">{NEVER_IDLE_NOTE}</p>
      </section>

      <section className="section">
        <h2 className="section__title">Folders</h2>
        <MountsList mounts={data.mounts} />
        {/* Where the tasks of one run meet, and where they do not. */}
        {data.tasks > 1 ? (
          <p className="form__note">{SCRATCH_PER_MACHINE_NOTE}</p>
        ) : null}
      </section>

      {data.tasks > 1 ? (
        <section className="section" aria-labelledby="task-list-title">
          <h2 className="section__title" id="task-list-title">
            Its tasks
          </h2>
          <TaskList run={data} onShowLog={showLog} />
        </section>
      ) : null}

      <section className="section">
        <h2 className="section__title">What has happened</h2>
        <EventTimeline events={data.events} />
      </section>

      <section className="section">
        <h2 className="section__title" id="run-log-title" tabIndex={-1} ref={logTitle}>
          {perTaskLogs ? `Log of task ${task}` : "Log"}
        </h2>
        {perTaskLogs ? (
          <Field
            label="Task"
            htmlFor="log-task"
            hint={
              `0 to ${data.tasks - 1} — the number the task read as $TASK_ID. ` +
              `Every task keeps its own log and there is no merged one: a ` +
              `thousand of them interleaved would be a file in which none is ` +
              `readable. A task that has not started yet says so, rather than ` +
              `showing another task's output.`
            }
          >
            <input
              id="log-task"
              className="input input--narrow"
              type="number"
              min={0}
              max={data.tasks - 1}
              value={task}
              onChange={(event) => chooseTask(event.target.value)}
            />
          </Field>
        ) : null}
        {perTaskLogs && data.tasks_failed > 0 ? (
          // The run counts what failed; it is the log of one of them that says
          // why, so the sentence sends the reader to the number above.
          <p className="form__note">
            {`${pluralize(data.tasks_failed, "task")} of this run failed. ` +
              `Each one kept its own log — open it by its number. What the ` +
              `others wrote is untouched: a failed task never removes it, ` +
              `never stops the tasks still running and never cancels the ` +
              `ones not started yet.`}
          </p>
        ) : null}
        {!data.has_logs ? (
          // Logs are read from the container that wrote them; nothing copies
          // them anywhere. When the machine goes back, they go with it.
          <p className="state-block">
            There is no log to read. A log lives in the machine that wrote it
            and goes back with it, so a run that has not started yet has none —
            and neither has one whose machine has already been given back.
          </p>
        ) : logs.isPending ? (
          <LoadingBlock label="Reading the log…" />
        ) : logs.isError ? (
          <ErrorBlock error={logs.error} onRetry={() => void logs.refetch()} />
        ) : (
          <pre className="log">{logs.data || "Nothing has been written yet."}</pre>
        )}
      </section>

      {/* Built from this run's own published fields and from nothing else —
          no default this page happens to hold, and no flag for a value the
          run does not carry. */}
      {nkCommand !== null ? (
        <section className="section" aria-labelledby="run-again-title">
          <h2 className="section__title" id="run-again-title">
            Run it again
          </h2>
          <CopyBlock value={nkCommand} label="Copy the nk command" />
          <p className="form__note">{REPRODUCE_NOTE}</p>
        </section>
      ) : null}

      {dialog === "extend" ? (
        <Dialog
          title="Give this run more time"
          onClose={close}
          footer={
            <>
              <button type="button" className="btn" onClick={close}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={extend.isPending}
                onClick={() =>
                  extend.mutate({ hours }, { onSuccess: () => setDialog(null) })
                }
              >
                {extend.isPending ? "Adding…" : `Add ${pluralize(hours, "hour")}`}
              </button>
            </>
          }
        >
          <p className="dialog__text">
            It currently stops in {formatRemaining(data.expires_at)}, finished
            or not. How much longer does it need?
          </p>
          <fieldset className="choice-row">
            <legend className="sr-only">How many more hours</legend>
            {EXTEND_PRESETS.map((preset) => (
              <label
                key={preset}
                className="choice"
                data-selected={hours === preset}
              >
                <input
                  type="radio"
                  name="extend-hours"
                  className="sr-only"
                  value={preset}
                  checked={hours === preset}
                  onChange={() => setHours(preset)}
                />
                +{preset} h
              </label>
            ))}
          </fieldset>
          {extend.isError ? (
            <p className="dialog__error" role="alert">
              {errorMessage(extend.error)}
            </p>
          ) : null}
        </Dialog>
      ) : null}

      {dialog === "stop" ? (
        <Dialog
          title="Stop this run"
          onClose={close}
          footer={
            <>
              <button type="button" className="btn" onClick={close}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={cancel.isPending}
                onClick={() =>
                  cancel.mutate(undefined, {
                    onSuccess: () => setDialog(null),
                  })
                }
              >
                {cancel.isPending ? "Stopping…" : "Stop it"}
              </button>
            </>
          }
        >
          {/* What is kept depends on where this run was working, so the
              dialog asks the run rather than promising everybody a home
              folder a fan-out never had. */}
          <p className="dialog__text">
            {data.tasks > 1
              ? "Every task is stopped and the machines go back immediately. " +
                "/work is deleted with the run, and each task's fast local " +
                "folder goes with its machine; what a task has already pushed " +
                "somewhere else is kept."
              : "The command is stopped and the machine goes back " +
                "immediately. Whatever it has already written to your home " +
                "folder is kept; whatever is in the fast local folder goes " +
                "with the machine."}
          </p>
          {cancel.isError ? (
            <p className="dialog__error" role="alert">
              {errorMessage(cancel.error)}
            </p>
          ) : null}
        </Dialog>
      ) : null}
    </>
  );
}
